import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

export type LpButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type LpButtonSize = 'sm' | 'md';

/**
 * Reusable button for the Lead Pipelines flow. Centralises the violet primary /
 * bordered secondary / ghost / danger variants so individual templates don't
 * re-declare button classes (and don't accidentally double up icons + text).
 *
 * Renders a single `<button>`; pass `[link]` for navigation (router handles the
 * click) or bind `(pressed)` for an action. Put the icon + label as content:
 *   <app-lp-button variant="primary" [link]="['/leads/pipelines/new']">
 *     <svg .../> New pipeline
 *   </app-lp-button>
 */
@Component({
  selector: 'app-lp-button',
  standalone: true,
  imports: [CommonModule, RouterModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      [type]="type"
      [disabled]="disabled"
      [routerLink]="link"
      (click)="pressed.emit($event)"
      class="group inline-flex items-center justify-center gap-1.5 rounded-lg text-sm tracking-wide transition disabled:cursor-not-allowed disabled:opacity-50"
      [ngClass]="variantClass + ' ' + sizeClass">
      <ng-content></ng-content>
    </button>
  `,
})
export class LpButtonComponent {
  @Input() variant: LpButtonVariant = 'primary';
  @Input() size: LpButtonSize = 'md';
  @Input() type: 'button' | 'submit' = 'button';
  @Input() disabled = false;
  /** When set, the button navigates via routerLink; otherwise bind `(pressed)`. */
  @Input() link: string | any[] | null = null;

  @Output() pressed = new EventEmitter<MouseEvent>();

  // Filled primary uses the shared `.btn-leads` class (violet equivalent of `.btn-blue`,
  // with bg-opacity hover). The outline variants dim to 90% opacity on hover; `enabled:`
  // keeps that from fighting `disabled:opacity-50`, and the base `transition` animates it.
  get variantClass(): string {
    switch (this.variant) {
      case 'secondary':
        return 'border border-gray-200 bg-white text-secondary dark:border-gray-600 dark:bg-gray-800 enabled:hover:opacity-90';
      case 'ghost':
        return 'text-secondary enabled:hover:opacity-90';
      case 'danger':
        return 'bg-red-500 text-white shadow-lg shadow-red-500/10 enabled:hover:bg-opacity-90';
      case 'primary':
      default:
        return 'btn-leads';
    }
  }

  get sizeClass(): string {
    return this.size === 'sm' ? 'px-3 py-1.5' : 'px-6 py-2';
  }
}
